/**
 * A staged wager, as the chips it is made of. A phone that says `130` is
 * accounting; a phone that shows a hundred, a quarter and a nickel is a
 * table. The pile is drawn tallest-first, so the chips that carry the stake
 * sit at the bottom and the small change rides on top — which is also the
 * chip a tap takes back.
 */
import { CHIP_DENOMS, type ChipDenom } from '@tv-casino/shared';

/** Denominations, biggest first: the order a pile is built in. */
const TALLEST_FIRST = [...CHIP_DENOMS].sort((a, b) => b - a);

export interface ChipStackVM {
  /** The pile, bottom chip first. Never longer than `max`. */
  chips: ChipDenom[];
  /** Chips the pile could not show — the web's `+N` over a deep stack. */
  hidden: number;
  /** What the pile is worth, printed under it. */
  total: number;
  /** The chip a take-back removes, or null when there is nothing staged. */
  top: ChipDenom | null;
}

/**
 * The pile for `total`, made of the house's denominations, greedily and
 * biggest first — the way a dealer colours a stake up. Anything under the
 * smallest chip is not a stake a table can take, so it rounds down and the
 * remainder simply is not drawn.
 */
export function chipStack(total: number, { max = 6 }: { max?: number } = {}): ChipStackVM {
  const chips: ChipDenom[] = [];
  let left = Math.max(0, Math.floor(total));
  for (const denom of TALLEST_FIRST) {
    while (left >= denom) {
      chips.push(denom);
      left -= denom;
    }
  }
  const shown = chips.slice(0, Math.max(0, max));
  return {
    chips: shown,
    hidden: chips.length - shown.length,
    total: Math.max(0, Math.floor(total)),
    // The top of the pile is the last chip laid on it, shown or not: a tap
    // takes back a real chip, never one the pile happened to have room for.
    top: chips.at(-1) ?? null,
  };
}

/**
 * The chips in the rail, smallest first. Anything under the table minimum is
 * not a chip this table will take, so it is filtered out — and a minimum that
 * falls between denominations gets its own chip at the front, because a rail
 * whose smallest chip is over the minimum cannot bet the minimum.
 */
export function chipTray(minBet: number): number[] {
  const chips = CHIP_DENOMS.filter((d) => d >= minBet);
  return chips[0] === minBet ? [...chips] : [minBet, ...chips];
}

/**
 * The denomination face an amount wears in the rail. A table minimum that
 * falls between chips still has to be drawn as clay, so it borrows the face
 * below it — the same buckets the web's `chipTone` paints.
 */
export const chipFace = (amount: number): ChipDenom =>
  amount >= 500 ? 500 : amount >= 100 ? 100 : amount >= 25 ? 25 : 5;
